import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { useImmerReducer } from "use-immer";
import io from "socket.io-client";

import Nav from "./components/Nav.js";
import Page from "./components/Page.js";
import Tray from "./components/Tray.js";
import Menu from "./components/Menu.js";
import Login from "./components/Login.js";
import Register from "./components/Register.js";
import Notification from "./components/Notification.js";
import SplashScreen from "./components/SplashScreen.js";
import About from "./components/About.js";
import Terms from "./components/Terms.js";
import Privacy from "./components/Privacy.js";
import Dispute from "./components/Dispute.js";
import Account from "./components/Account.js";

// Componentă simplă cu logo pentru afișarea în tray la pornire
const TrayLogo = () => (
  <div style={{ textAlign: "center" }}>
    <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" style={{ maxWidth: "200px" }} />
  </div>
);

import Vcarousel from "./components/Vcarousel.js";

import StateContext from "./StateContext.js";
import DispatchContext from "./DispatchContext.js";

import "./style.css";

function App() {
  console.log("[APP] App render");
  const initialState = {
    config: {},
    orientation: "landscape",
    activeEvent: { header: "", message: "", link: "" },
    activeStation: 0,
    muted: true,
    isPlayerPlaying: false,
    isTrayVisible: true,
    trayContent: null,
    isTimerStopped: false,
    isLoggedIn: false,
    userData: null,
    isInitialLoad: true,
    showSplash: false, // nu mai folosim overlay-ul separat
    showMenuFade: false,
    isBlocked: false,
    videoVisible: false,
  };

  function appReducer(draft, action) {
    switch (action.type) {
      case "setConfig": {
        draft.config = action.payload;
        return;
      }
      case "setOrientation": {
        draft.orientation = action.value;
        return;
      }
      case "newEvent": {
        draft.activeEvent = action.value;
        return;
      }
      case "setActiveStation": {
        draft.activeStation = action.value;
        return;
      }
      case "toggleMute": {
        draft.muted = !draft.muted;
        return;
      }
      case "setTrayVisible": {
        draft.isTrayVisible = action.value;
        if (action.value) {
          draft.isInitialLoad = false;
        }
        return;
      }
      case "playerPlaying": {
        draft.isPlayerPlaying = true;
        return;
      }
      case "setTrayContent": {
        draft.trayContent = action.value;
        return;
      }
      case "stopTimer": {
        draft.isTimerStopped = true;
        return;
      }
      case "resetTimer": {
        draft.isTimerStopped = false;
        return;
      }
      case "userLoggedIn": {
        draft.isLoggedIn = true;
        draft.userData = action.payload;
        return;
      }
      case "userLoggedOut": {
        draft.isLoggedIn = false;
        draft.userData = null;
        return;
      }
      case "setSplash": {
        draft.showSplash = action.value;
        return;
      }
      case "setMenuFade": {
        draft.showMenuFade = action.value;
        return;
      }
      case "setBlocked": {
        draft.isBlocked = action.value;
        return;
      }
      case "setVideoVisible": {
        draft.videoVisible = action.value;
        return;
      }
      default:
        return draft;
    }
  }

  const [state, dispatch] = useImmerReducer(appReducer, initialState);
  const sliderRef = useRef(null);

  // Debug: logăm la fiecare render
  console.log("[DEBUG] Render App, isLoggedIn:", state.isLoggedIn, "showSplash:", state.showSplash, "isBlocked:", state.isBlocked);

  // Verificăm dacă există un token salvat în localStorage la încărcarea aplicației
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('userData');
    
    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        dispatch({ type: "userLoggedIn", payload: userData });
      } catch (error) {
        console.error('Eroare la parsarea datelor utilizatorului:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      }
    }
  }, []);

  // Get config.json
  useEffect(() => {
    fetch("./config.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        let tmp = data.map((station) => ({ ...station }));

        const fetchPromises = tmp.map((station, i) =>
          fetch(station.designConfig)
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
            .then((config) => {
              tmp[i] = {
                ...tmp[i],
                portraitUrl: config[0].portraitUrl,
                landscapeUrl: config[0].landScapeUrl,
              };
            })
            .catch((error) => {
              console.error(
                `Error loading station config for ${station.name}:`,
                error
              );
            })
        );

        Promise.all(fetchPromises)
          .then(() => {
            dispatch({ type: "setConfig", payload: tmp });
          })
          .catch((error) => {
            console.error("Error loading all configs:", error);
          });
      })
      .catch((error) => {
        console.error("Eroare la fetch ./config.json:", error);
        alert("Eroare la încărcarea config.json! Vezi consola pentru detalii.");
      });
  }, [dispatch]);

  // Set aspect ratio
  useEffect(() => {
    if (state.showSplash || state.isBlocked) return;
    function adjustAspectRatio() {
      const aspectRatioDiv = document.querySelector(".aspect-ratio");
      if (!aspectRatioDiv) return;
      const body = document.body;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (viewportWidth > viewportHeight) {
        dispatch({ type: "setOrientation", value: "landscape" });
        body.classList.remove("portrait");
        body.classList.add("landscape");
        const width = viewportHeight * (16 / 9);
        const height = viewportHeight;
        if (width > viewportWidth) {
          aspectRatioDiv.style.width = `${viewportWidth}px`;
          aspectRatioDiv.style.height = `${viewportWidth / (16 / 9)}px`;
        } else {
          aspectRatioDiv.style.width = `${width}px`;
          aspectRatioDiv.style.height = `${height}px`;
        }
      } else {
        dispatch({ type: "setOrientation", value: "portrait" });
        body.classList.remove("landscape");
        body.classList.add("portrait");
        const height = viewportWidth * (16 / 9);
        const width = viewportWidth;
        if (height > viewportHeight) {
          aspectRatioDiv.style.width = `${viewportHeight / (16 / 9)}px`;
          aspectRatioDiv.style.height = `${viewportHeight}px`;
        } else {
          aspectRatioDiv.style.width = `${width}px`;
          aspectRatioDiv.style.height = `${height}px`;
        }
      }

      aspectRatioDiv.style.position = "absolute";
      aspectRatioDiv.style.top = "50%";
      aspectRatioDiv.style.left = "50%";
      aspectRatioDiv.style.transform = "translate(-50%, -50%)";
    }

    window.addEventListener("resize", adjustAspectRatio);
    window.addEventListener("load", adjustAspectRatio);

    adjustAspectRatio();

    return () => {
      window.removeEventListener("resize", adjustAspectRatio);
      window.removeEventListener("load", adjustAspectRatio);
    };
  }, [dispatch, state.showSplash, state.isBlocked]);

  // Stop timer on menu click
  const handleMenuClick = () => {
    dispatch({ type: "stopTimer" });
  };

  // Catch events from WS - lazy load socket.io connection only when needed
  useEffect(() => {
    let socket;
    // Inițializăm socket-ul doar dacă utilizatorul este autentificat
    if (state.isLoggedIn) {
      console.log("[WS] state.isLoggedIn este TRUE, inițializez socket la:", process.env.WS_ADDRESS);
      socket = io(process.env.WS_ADDRESS);

      socket.on("newsyncurl", (data) => {
        console.log("[WS] Primit newsyncurl:", data);
        try {
          let header = "";
          let message = "";
          let link = "";

          if (data.event === "App\\Events\\NunzioStoriesEvent") {
            header = "Citește pe alephnews.ro:";
            message = data.data.message.title;
            if (data.data.message.an_id != null) {
              link = "https://alephnews.ro/?p=" + data.data.message.an_id;
            } else {
              link = data.data.message.forced_link;
            }
          }

          if (data.event === "App\\Events\\NunzioAdsEvent") {
            header = "RECLAMĂ";
            message = "Vizitează clientul nostru";
            if (data.data.message.client != null) {
              message += data.data.message.client;
            }
            link = data.data.message.ad_url;
          }

          let payload = {
            header: header,
            message: message,
            link: link,
          };

          if (!state.isTrayVisible) {
            dispatch({
              type: "setTrayContent",
              value: () => <Notification payload={payload} />,
            });
            dispatch({ type: "setTrayVisible", value: true });

            handleMenuClick();

            setTimeout(() => {
              dispatch({ type: "setTrayVisible", value: false });
              dispatch({ type: "setTrayContent", value: Menu });
            }, 5000);
          } else {
            console.log("Ignoring event: ", payload);
          }
        } catch (err) {
          console.error("Eroare în handlerul newsyncurl:", err, data);
        }
      });

      socket.on("error", (err) => {
        console.error("[WS] Socket error:", err);
      });
      socket.on("connect_error", (err) => {
        console.error("[WS] Socket connect_error:", err);
      });
    } else {
      console.log("[WS] state.isLoggedIn este FALSE, nu inițializez socket");
    }
    return () => {
      if (socket) {
        console.log("[WS] Cleanup socket.io...");
        socket.off("newsyncurl");
        socket.off("error");
        socket.off("connect_error");
        socket.disconnect();
      } else {
        console.log("[WS] Cleanup fără socket activ");
      }
    };
  }, [state.isLoggedIn, dispatch]);

  useEffect(() => {
    if (sliderRef.current && sliderRef.current.slickGoTo) {
      sliderRef.current.slickGoTo(state.activeStation);
    }
  }, [state.activeStation]);

  useEffect(() => {
    if (state.isLoggedIn && state.isInitialLoad) {
      const timer = setTimeout(() => {
        dispatch({ type: "setTrayVisible", value: false });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [state.isLoggedIn, state.isInitialLoad]);

  // SECVENȚĂ INIȚIALĂ SIMPLĂ: tray vizibil cu Menu; fade-in opțiuni gestionat în Tray
  useEffect(() => {
    dispatch({ type: "setTrayVisible", value: true });
    dispatch({ type: "setTrayContent", value: Menu });
  }, []);

  // Retract tray after 5 seconds unless timer stopped
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!state.isTimerStopped) {
        dispatch({ type: "setTrayVisible", value: false });
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Adaug un useEffect separat pentru delay de 20s la verificarea autentificării
  useEffect(() => {
    const authTimer = setTimeout(() => {
      checkAuthAndSubscription();
    }, 20000); // 20 secunde
    return () => clearTimeout(authTimer);
  }, []);

  async function checkAuthAndSubscription() {
    const token = localStorage.getItem('authToken');
    if (!token) {
      dispatch({ type: "userLoggedOut" });
      dispatch({ type: "setTrayVisible", value: true });
      dispatch({ type: "setTrayContent", value: Login });
      return;
    }
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem('userData') || '{}');
    } catch {
      user = null;
    }
    if (!user || !user.email) {
      dispatch({ type: "userLoggedOut" });
      dispatch({ type: "setTrayVisible", value: true });
      dispatch({ type: "setTrayContent", value: Login });
    } else {
      try {
        const res = await fetch("https://backend.alephmedia.ro/api/account/check-apple-subscription", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ email: user.email })
        });
        const data = await res.json();
        if (!data.success || data.data.apple_subscription_status !== 'active') {
          dispatch({ type: "userLoggedOut" });
          dispatch({ type: "setTrayVisible", value: true });
          dispatch({ type: "setTrayContent", value: Login });
        } else {
          dispatch({ type: "userLoggedIn", payload: user });
          dispatch({ type: "setBlocked", value: false });
        }
      } catch {
        dispatch({ type: "userLoggedOut" });
        dispatch({ type: "setTrayVisible", value: true });
        dispatch({ type: "setTrayContent", value: Login });
      }
    }
  }

  // After mount, make Vcarousel visible after 2s
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch({ type: "setVideoVisible", value: true });
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Blocare interacțiuni dacă isBlocked
  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {state.isBlocked ? (
          <div className="blocked-overlay">
            <div className="blocked-message">
              <h2>Acces restricționat</h2>
              <p>Trebuie să fii autentificat cu o subscripție activă pentru a accesa conținutul.</p>
            </div>
            <Tray TrayContent={() => <div />} /> {/* nu mai montăm Login ca trayContent */}
            <Login /> {/* Login doar aici, ca overlay */}
          </div>
        ) : state.showSplash ? (
          <div className="splash-screen">
            <img src="/assets/img/logo-aleph-rosu.png" alt="Aleph" />
          </div>
        ) : (
          <BrowserRouter>
            <Page>
              <Nav />
              <Tray TrayContent={state.trayContent} />
              {state.videoVisible && <Vcarousel ref={sliderRef} />}
            </Page>
          </BrowserRouter>
        )}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

const container = document.getElementById("app");

if (!container._reactRootContainer) {
  const root = ReactDOM.createRoot(container);
  root.render(<App />);
  container._reactRootContainer = root;
} else {
  container._reactRootContainer.render(<App />);
}

if (module.hot) {
  module.hot.accept();
}

export default App;
